/**
 * ÉCHEC DE NUMÉROTATION — vocabulaire commun au SDK, au push serveur et à l'UI.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LE CONSTAT DE RECETTE (2026-07-28) QUI A CRÉÉ CE MODULE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Un numéro invalide composé en campagne : le backend tranche `BUSY` en ~4 s,
 * pendant que le SDK Twilio remonte un **31005** (« hangup » de la passerelle)
 * porté par le leg parent. Deux faits pour un seul événement — et la scène
 * traitait le second comme une PANNE DU POSTE :
 *
 *  - bandeau « Connexion au service téléphonique perdue » allumé à tort ;
 *  - `callMachine.fail()` ⇒ retour à `idle` **en écrasant** le `wrapup` que le
 *    statut backend venait d'ouvrir (ou l'inverse, selon l'ordre d'arrivée) ;
 *  - donc aucune qualification possible ⇒ `updateAfterCall()` jamais appelé ⇒
 *    `GET /contacts/next` re-servait **le même contact** ⇒ l'enchaînement
 *    tournait en rond sur le mauvais numéro. C'est le « gel » observé.
 *
 * La règle posée ici : **un leg qui n'aboutit pas n'est pas un poste en
 * panne, c'est un appel qui a échoué.** Il se QUALIFIE (campagne) ou rend le
 * composeur (manuel) — cf. `callMachine.dialFailed()`.
 *
 * ⚠️ `CallOutcome` (schema.prisma) n'a PAS de valeur `BUSY` : les issues sont
 * `PENDING · NO_ANSWER · INTERESTED · NOT_INTERESTED · CALLBACK ·
 * ALREADY_DONATED · WRONG_NUMBER · VOICEMAIL`. Un numéro occupé se qualifie
 * donc en `NO_ANSWER` — ce qui est aussi le bon choix métier : c'est l'issue
 * que `updateAfterCall()` fait REJOUER (retry programmé), alors que
 * `WRONG_NUMBER` exclut définitivement le contact de la campagne. Modifier
 * l'enum demanderait une migration : hors périmètre (schema interdit).
 */

/** Cause lisible de l'échec. Sert de clé i18n : `dialFailure.<reason>`. */
export type DialFailureReason =
  /** Le poste n'a pas su dire pourquoi (erreur SDK seule, statut pas encore là). */
  | 'unreachable'
  /** Le backend a tranché `BUSY`. */
  | 'busy'
  /** Le backend a tranché `NO_ANSWER`. */
  | 'no-answer'
  /** Le backend a tranché `FAILED` — numéro invalide, injoignable. */
  | 'failed'

/**
 * Issue proposée en UN clic à l'agent. Sous-ensemble volontairement étroit de
 * `CallOutcome` : seules les deux issues qu'un échec de numérotation permet
 * d'affirmer sans avoir parlé à personne.
 */
export type SuggestedOutcome = 'NO_ANSWER' | 'WRONG_NUMBER'

/**
 * Famille de la CAUSE d'échec, telle que le serveur la décline à partir du
 * code Twilio persisté (issue #144, E-03). Miroir de `FailureCategory` du
 * catalogue backend (`telephony/twilio-error-catalog.ts`) — le serveur classe,
 * la scène traduit : clé i18n `dialFailure.cause.<catégorie>`.
 */
export type FailureCategory =
  | 'ACCOUNT_SUSPENDED'
  | 'GEO_BLOCKED'
  | 'INVALID_NUMBER'
  | 'STATION'
  | 'CARRIER'
  | 'UNKNOWN'

export interface DialFailureCause {
  /** Code brut (`13227`, `31005`, `SIP-404`…) — affiché pour le support et le cas inconnu. */
  code: string
  category: FailureCategory
}

/** Forme du push serveur : la cause n'existe que si le code ET la catégorie sont là. */
export function readFailureCause(event: {
  twilioErrorCode?: string | null
  failureCategory?: FailureCategory | null
}): DialFailureCause | null {
  if (!event.twilioErrorCode || !event.failureCategory) return null
  return { code: event.twilioErrorCode, category: event.failureCategory }
}

export interface DialFailure {
  reason: DialFailureReason
  /**
   * CAUSE précise remontée par le serveur (#144) : crédit épuisé, pays bloqué,
   * numéro inexistant, poste… Absente tant que le code n'est pas connu — la
   * phrase générique de `reason` reste alors seule. Ne change ni l'issue
   * suggérée ni la mécanique de la scène : c'est une information de plus.
   */
  cause?: DialFailureCause | null
  /**
   * Issue MISE EN AVANT (bouton principal). `null` quand la cause n'est pas
   * encore connue : la scène propose alors le formulaire complet plutôt
   * qu'une issue devinée. Le push serveur (`call.updated`) l'enrichit dès
   * qu'il arrive — cf. `refineDialFailure()`.
   */
  suggestedOutcome: SuggestedOutcome | null
  /**
   * Issue ALTERNATIVE, offerte elle aussi en UN clic (bouton secondaire).
   *
   * Une suggestion n'est jamais une certitude : un opérateur peut répondre
   * « occupé » sur un numéro qui n'existe plus, et « échec » sur une ligne
   * simplement saturée. Le raccourci met donc une issue en avant SANS retirer
   * l'autre — c'est ce qui permet de garder un geste unique dans les deux cas,
   * au lieu de renvoyer l'agent au formulaire complet dès qu'il n'est pas
   * d'accord avec la machine.
   */
  alternativeOutcome: SuggestedOutcome | null
}

/**
 * Échec constaté par le SEUL poste (erreur de leg avant décroché). On ne sait
 * pas encore si c'est occupé ou invalide : on ne suggère donc rien.
 */
export const UNREACHABLE_DIAL_FAILURE: DialFailure = {
  reason: 'unreachable',
  suggestedOutcome: null,
  alternativeOutcome: null,
}

/**
 * Statuts `CallStatus` (schema.prisma) qui, AVANT tout décroché, signent un
 * échec de numérotation. `MISSED` n'en fait pas partie : il ne concerne que
 * les entrants.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * `failed` ≠ `busy` — L'ARBITRAGE UTILISATEUR DU 2026-07-30
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Twilio distingue déjà les deux dans le `DialCallStatus` de l'action `<Dial>`,
 * et la chaîne v2 conserve cette distinction jusqu'ici sans jamais l'aplatir
 * (`DIAL_STATUS_MAP` et `WEBHOOK_STATUS_MAP` de `call-lifecycle.service.ts`
 * mappent `busy → BUSY` et `failed → FAILED` séparément ; le push
 * `call.updated` transporte le `status` tel quel). Il ne restait qu'à
 * l'EXPLOITER, ce que la scène ne faisait qu'à moitié :
 *
 *  - **`failed` = le réseau a REFUSÉ le numéro** (invalide, plus attribué,
 *    hors service). Personne n'a sonné. Le rappeler ne servira jamais à rien :
 *    l'issue mise en avant est donc `WRONG_NUMBER`, qui EXCLUT le contact de
 *    la campagne (`updateAfterCall`). C'est l'intérêt principal exprimé par
 *    l'utilisateur : nettoyer le fichier au fil des appels.
 *  - **`busy` / `no-answer` = quelqu'un est bien au bout**, il est en ligne ou
 *    absent. L'issue mise en avant est `NO_ANSWER`, que `updateAfterCall`
 *    REPROGRAMME. Sortir ce contact de la campagne serait une perte sèche.
 *
 * Dans les deux cas l'autre issue reste à un clic (`alternativeOutcome`).
 *
 * ⚠️ `CallOutcome` (schema.prisma) n'a PAS de valeur « occupé » : `NO_ANSWER`
 * est bien l'issue à poser pour un `BUSY`, et ce n'est pas un pis-aller mais
 * le bon choix métier (retry programmé). Modifier l'enum demanderait une
 * migration : hors périmètre.
 */
const FAILURE_BY_STATUS: Record<string, DialFailure> = {
  BUSY: {
    reason: 'busy',
    suggestedOutcome: 'NO_ANSWER',
    alternativeOutcome: 'WRONG_NUMBER',
  },
  NO_ANSWER: {
    reason: 'no-answer',
    suggestedOutcome: 'NO_ANSWER',
    alternativeOutcome: 'WRONG_NUMBER',
  },
  FAILED: {
    reason: 'failed',
    suggestedOutcome: 'WRONG_NUMBER',
    alternativeOutcome: 'NO_ANSWER',
  },
}

/** `true` si ce statut backend est un échec de numérotation connu. */
export function isDialFailureStatus(status: string): boolean {
  return status in FAILURE_BY_STATUS
}

/**
 * Traduit un statut backend en échec qualifiable. Un statut inconnu retombe
 * sur l'échec générique plutôt que d'inventer une issue : mieux vaut demander
 * à l'agent que d'écrire une qualification fausse dans la campagne.
 */
export function describeDialFailure(
  status: string,
  cause?: DialFailureCause | null,
): DialFailure {
  const failure = FAILURE_BY_STATUS[status] ?? UNREACHABLE_DIAL_FAILURE
  // La clé n'est posée QUE si une cause existe : les appelants sans cause
  // reçoivent l'objet historique, inchangé (spécifications par égalité).
  return cause ? { ...failure, cause } : failure
}
